import { useRef } from "react";
import { motion, useScroll, useSpring } from "motion/react";
import { SectionHeading } from "../ui/SectionHeading";
import { Reveal } from "../ui/Reveal";
import { experience } from "../../data/content";

export function Experience() {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.75", "end 0.55"],
  });
  const lineScale = useSpring(scrollYProgress, { stiffness: 80, damping: 24 });

  return (
    <section id="journey" className="relative overflow-hidden bg-ink-2">
      <div className="pointer-events-none absolute right-0 top-0 h-[24rem] w-[24rem] rounded-full bg-secondary/10 blur-[130px]" />
      <div className="section">
        <div className="mx-auto max-w-5xl px-6 md:px-10">
          <SectionHeading eyebrow="04 · Journey" title="Experience that" accent="compounds." />

          <div ref={ref} className="relative">
            {/* Track + progress line */}
            <div className="absolute left-[7px] top-2 h-full w-px bg-white/10 md:left-[9px]" />
            <motion.div
              className="absolute left-[7px] top-2 w-px origin-top bg-gradient-to-b from-primary via-secondary to-accent md:left-[9px]"
              style={{ height: "100%", scaleY: lineScale }}
            />

            <div className="space-y-12 md:space-y-16">
              {experience.map((item, i) => (
                <Reveal key={item.company} delay={0.05} className="relative pl-10 md:pl-14">
                  {/* Dot */}
                  <span className="absolute left-0 top-2 grid h-[15px] w-[15px] place-items-center md:h-[19px] md:w-[19px]">
                    <span className="absolute h-full w-full rounded-full bg-primary/30 animate-ping [animation-duration:2.5s]" />
                    <span className="relative h-2.5 w-2.5 rounded-full bg-gradient-to-br from-primary to-accent md:h-3 md:w-3" />
                  </span>

                  <div className="rounded-3xl border border-white/10 bg-surface/50 p-7 backdrop-blur transition-colors duration-500 hover:border-white/20 md:p-9">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="rounded-full border border-primary/30 bg-primary/10 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.2em] text-primary">
                        {item.period}
                      </span>
                      <span className="font-mono text-xs uppercase tracking-[0.25em] text-muted">
                        {item.company}
                      </span>
                    </div>

                    <h3 className="mt-4 font-display text-2xl font-bold text-white md:text-3xl">
                      {item.role}
                    </h3>
                    <p className="mt-2 text-white/55">{item.summary}</p>

                    <ul className="mt-5 space-y-2.5">
                      {item.points.map((p) => (
                        <li key={p} className="flex items-start gap-3 text-sm text-white/65">
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gradient-to-r from-primary to-accent" />
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
